import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { catchError, forkJoin, of, Subscription } from 'rxjs';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { PageScrollSpyComponent, ScrollSpySection } from '../../../../shared/components/page-scroll-spy/page-scroll-spy.component';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { AutomacaoService } from '../../../../shared/services/apis/compras/automacao.service';
import { EtapasKanbanService } from '../../../../shared/services/apis/compras/etapas-kanban.service';
import { PerfilService } from '../../../../shared/services/apis/usuario/perfil.service';
import { UsuarioService } from '../../../../shared/services/apis/usuario/usuario.service';
import { AutomacaoRequest } from '../../../../shared/services/requests/compras/automacao.request';
import { AutomacaoDestinatarioRequest } from '@shared/services/requests/compras/automacao-destinatario.request';
import { AutomacaoDestinatarioStruct } from '@shared/services/structs/compras/automacao-destinatario.struct';
import { AutomacaoStruct } from '@shared/services/structs/compras/automacao.struct';
import { OrigemLancamentoEnum } from '@shared/enums/compras/origem-lancamento.enum';
import { TipoAutomacaoEnum } from '@shared/enums/compras/tipo-automacao.enum';
import { TipoDestinatarioNotificacaoEnum } from '@shared/enums/compras/tipo-destinatario-notificacao.enum';
import { TipoOcorrenciaCompraEnum } from '@shared/enums/compras/tipo-ocorrencia-compra.enum';

interface AutomacaoBase {
  id: string;
  icon: string;
  nome: string;
  descricao: string;
  categoria?: string;
  ativa: boolean;
}

interface AcaoAutomatica extends AutomacaoBase {
  idTipoAutomacao: TipoAutomacaoEnum;
  /** Gatilho inerente ao tipo: rótulo fixo, não é escolhido nem persistido. */
  gatilho: string;
  /** Dispara ao aprovar: sem a etapa Aprovação de Pedido ativa nunca roda. */
  exigeAprovacao?: boolean;
}

interface MovimentoAutomatico extends AutomacaoBase {
  idTipoOcorrenciaCompra: TipoOcorrenciaCompraEnum;
  quando: string;
  /** Etapa de destino por chave — o id vem de etapa_kanban, que é por empresa. */
  chaveDestino: string;
  /** Id já persistido do destino. Vale como fallback quando o lookup de etapas não veio:
   *  sem ele, uma falha de rede reenviaria a automação sem destino. */
  idEtapaKanbanDestinoSalvo: number | null;
  destino: string;
  corDestino: string;
}

/** Lançar estoque / Lançar financeiro: disparam sempre ao mover para Finalizado (gatilho fixo, não persistido). */
interface Lancamento extends AutomacaoBase {
  idTipoAutomacao: TipoAutomacaoEnum.AtualizarSaldoEstoque | TipoAutomacaoEnum.GerarContasPagar;
  idOrigemLancamento: OrigemLancamentoEnum | null;
  permissao: string;
  /** Nome curto da permissão, na pílula do cartão (o texto completo fica no tooltip). */
  permissaoNome: string;
}

interface NotificacaoAutomatica extends AutomacaoBase {
  /** Destinatários já no formato do payload: tipo + perfil/usuário quando houver. */
  destinatarios: AutomacaoDestinatarioRequest[];
  idTipoOcorrenciaCompra: TipoOcorrenciaCompraEnum | null;
  /** Linha correspondente no banco. Nulo enquanto o card não foi salvo — o backend faz
   *  upsert por esse id, então perdê-lo recriaria a automação com outro id. */
  idAutomacao: number | null;
}

interface DestinatarioOpcao {
  destinatario: AutomacaoDestinatarioRequest;
  label: string;
  icon: string;
  /** Papel resolvido por card (responsável/solicitante), não uma pessoa fixa. */
  dinamico: boolean;
}

@Component({
  selector: 'app-automacoes-compras',
  standalone: true,
  imports: [
    FormsModule,
    RouterModule,
    HeaderComponent,
    PageScrollSpyComponent,
    EmptyStateComponent,
    LoadingBoxComponent,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTooltipModule,
  ],
  templateUrl: './automacoes-compras.component.html',
  styleUrls: ['./automacoes-compras.component.css'],
})
export class AutomacoesComprasComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Suprimentos;

  public carregando = false;
  public salvando = false;
  private salvarPendente = false;

  /**
   * A configuração do servidor foi aplicada. Enquanto for falso a tela mostra o catálogo
   * desligado, que NÃO é o estado real: salvar dali gravaria tudo pausado e apagaria a
   * configuração da empresa. Por isso o salvamento fica bloqueado até um carregamento válido.
   */
  private configCarregada = false;

  private readonly subs = new Subscription();

  constructor(
    private automacaoService: AutomacaoService,
    private etapasKanbanService: EtapasKanbanService,
    private perfilService: PerfilService,
    private usuarioService: UsuarioService,
    private alertService: AlertService,
  ) {}

  ngOnInit(): void {
    this.carregar();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  public readonly sections: ScrollSpySection[] = [
    { id: 'acoes', label: 'Ações automáticas' },
    { id: 'movimentacoes', label: 'Movimentações automáticas' },
    { id: 'lancamentos', label: 'Lançamentos' },
    { id: 'notificacoes', label: 'Notificações' },
  ];

  // Sequência monotônica para ids de novas notificações — nunca reutiliza, mesmo após remoções.
  private proximoIdNotificacao = 1;

  /** Gatilhos selecionáveis — subconjunto de tipo_ocorrencia_compra que faz sentido automatizar. */
  public readonly gatilhos: { id: TipoOcorrenciaCompraEnum; label: string }[] = [
    { id: TipoOcorrenciaCompraEnum.Aprovacao, label: 'Ao aprovar o pedido' },
    { id: TipoOcorrenciaCompraEnum.Reprovacao, label: 'Ao rejeitar o pedido' },
    { id: TipoOcorrenciaCompraEnum.EnvioCotacao, label: 'Ao enviar a cotação' },
    { id: TipoOcorrenciaCompraEnum.EnvioPedidoFornecedor, label: 'Ao enviar o pedido ao fornecedor' },
    { id: TipoOcorrenciaCompraEnum.VinculoNFeEntrada, label: 'Ao vincular uma nota' },
    { id: TipoOcorrenciaCompraEnum.ConclusaoConferencia, label: 'Ao concluir a conferência' },
    { id: TipoOcorrenciaCompraEnum.DivergenciaRegistrada, label: 'Ao registrar uma divergência' },
    { id: TipoOcorrenciaCompraEnum.Finalizacao, label: 'Ao finalizar a compra' },
    { id: TipoOcorrenciaCompraEnum.Cancelamento, label: 'Ao cancelar a compra' },
  ];

  public readonly gatilhoLancamento = 'Ao mover para Finalizado';

  public readonly OrigemLancamentoEnum = OrigemLancamentoEnum;

  public readonly origensLancamento = [
    { id: OrigemLancamentoEnum.NotaFiscal, label: 'Pela Nota Fiscal', icon: 'receipt_long', hint: 'Os dados do lançamento vêm da nota fiscal' },
    { id: OrigemLancamentoEnum.Pedido, label: 'Pelo Pedido', icon: 'shopping_cart', hint: 'Os dados do lançamento vêm do pedido de compra' },
  ];

  // Destinatários dinâmicos do card (papéis resolvidos por card, não são perfis do sistema).
  public readonly destinatariosDoCard: DestinatarioOpcao[] = [
    { destinatario: { idTipoDestinatarioNotificacao: TipoDestinatarioNotificacaoEnum.Responsavel }, label: 'Responsável pelo card', icon: 'assignment_ind', dinamico: true },
    { destinatario: { idTipoDestinatarioNotificacao: TipoDestinatarioNotificacaoEnum.Solicitante }, label: 'Solicitante', icon: 'person_pin', dinamico: true },
  ];

  // Perfis e usuários reais do sistema, carregados por lookup.
  public destinatariosPorPerfil: DestinatarioOpcao[] = [];
  public destinatariosPorUsuario: DestinatarioOpcao[] = [];

  // Etapas da empresa, para resolver o destino das movimentações automáticas.
  private idEtapaPorChave = new Map<string, number>();

  /**
   * Etapa Aprovação de Pedido ativa na empresa. Sem o lookup de etapas assume ativa: bloquear o
   * toggle por falta de dado esconderia uma automação que pode estar funcionando.
   */
  private aprovacaoAtiva = true;

  /**
   * Automação que só dispara na aprovação, com a etapa desativada: fica desligada e bloqueada.
   * A API já a desliga ao desativar a etapa e recusa religá-la enquanto a etapa estiver inativa.
   */
  public acaoBloqueada(acao: AcaoAutomatica): boolean {
    return !!acao.exigeAprovacao && !this.aprovacaoAtiva;
  }

  // Ícone por perfil — best-effort por nome; perfis fora do mapa usam o ícone genérico.
  private readonly iconePorPerfil: { [nome: string]: string } = {
    'comprador': 'shopping_cart',
    'aprovador': 'how_to_reg',
    'financeiro': 'payments',
    'fiscal': 'receipt_long',
    'gestor de compras': 'supervisor_account',
  };

  public movimentoIncompleto(movimento: MovimentoAutomatico): boolean {
    return movimento.ativa && !this.idEtapaDestino(movimento);
  }

  private idEtapaDestino(movimento: MovimentoAutomatico): number | null {
    return this.idEtapaPorChave.get(movimento.chaveDestino) ?? movimento.idEtapaKanbanDestinoSalvo ?? null;
  }

  /** Ativo sem origem: a API recusa gravar, então o salvamento é bloqueado com aviso. */
  public lancamentoIncompleto(lancamento: Lancamento): boolean {
    return lancamento.ativa && !lancamento.idOrigemLancamento;
  }

  /** Ativa mas sem gatilho ou sem destinatário: nunca vai notificar ninguém. */
  public notificacaoIncompleta(notificacao: NotificacaoAutomatica): boolean {
    return notificacao.ativa && (!notificacao.idTipoOcorrenciaCompra || notificacao.destinatarios.length === 0);
  }

  /** O select guarda objetos: os gravados não são as mesmas instâncias das opções. */
  public mesmoDestinatario = (a: AutomacaoDestinatarioRequest | null, b: AutomacaoDestinatarioRequest | null): boolean =>
    !!a && !!b
    && a.idTipoDestinatarioNotificacao === b.idTipoDestinatarioNotificacao
    && (a.idPerfil ?? null) === (b.idPerfil ?? null)
    && (a.idUsuario ?? null) === (b.idUsuario ?? null);

  /** Rótulo/ícone de um destinatário já escolhido. */
  public metaDestinatario(destinatario: AutomacaoDestinatarioRequest): DestinatarioOpcao {
    const conhecido = [...this.destinatariosDoCard, ...this.destinatariosPorPerfil, ...this.destinatariosPorUsuario]
      .find(o => this.mesmoDestinatario(o.destinatario, destinatario));
    if (conhecido) return conhecido;

    // Perfil ou usuário excluído depois de configurado: mostra o id em vez de sumir,
    // senão o chip some sem o usuário entender por quê.
    const label = destinatario.idTipoDestinatarioNotificacao === TipoDestinatarioNotificacaoEnum.Usuario
      ? `Usuário ${destinatario.idUsuario}`
      : `Perfil ${destinatario.idPerfil}`;
    return { destinatario, label, icon: 'badge', dinamico: false };
  }

  // Catálogo fixo: nasce desligado e só liga com configuração salva pela empresa.
  public acoes: AcaoAutomatica[] = [
    {
      id: 's1a',
      idTipoAutomacao: TipoAutomacaoEnum.EnviarCotacaoFornecedor,
      icon: 'mail',
      nome: 'Solicitação de envio de formulário de cotação para fornecedor',
      descricao: 'Ao salvar a compra, sugere enviar o formulário aos fornecedores que ainda não receberam e pede confirmação.',
      gatilho: 'Ao salvar a compra',
      ativa: false,
    },
    {
      id: 's1b',
      idTipoAutomacao: TipoAutomacaoEnum.EnviarPedidoFornecedor,
      icon: 'send',
      nome: 'Enviar pedido ao fornecedor',
      descricao: 'Dispara o pedido por e-mail; sem e-mail cadastrado, gera PDF para envio manual.',
      gatilho: 'Ao aprovar o pedido',
      exigeAprovacao: true,
      ativa: false,
    },
  ];

  public movimentos: MovimentoAutomatico[] = [
    {
      id: 'm2',
      icon: 'fact_check',
      nome: 'Conferência concluída',
      descricao: 'Move o card automaticamente para Finalizado assim que a conferência for concluída.',
      categoria: 'Movimentação',
      idTipoOcorrenciaCompra: TipoOcorrenciaCompraEnum.ConclusaoConferencia,
      quando: 'Ao concluir a conferência',
      chaveDestino: 'finalizado',
      idEtapaKanbanDestinoSalvo: null,
      destino: 'Finalizado',
      corDestino: '#2E7D6B',
      ativa: false,
    },
  ];

  public lancamentos: Lancamento[] = [
    {
      id: 's1c',
      idTipoAutomacao: TipoAutomacaoEnum.AtualizarSaldoEstoque,
      icon: 'inventory_2',
      nome: 'Lançar estoque',
      descricao: 'Lança a entrada de estoque com as quantidades recebidas e conferidas, a partir da origem escolhida.',
      categoria: 'Estoque',
      idOrigemLancamento: OrigemLancamentoEnum.NotaFiscal,
      permissao: 'Requer a permissão Inserir do Processo de Compras.',
      permissaoNome: 'Inserir',
      ativa: false,
    },
    {
      id: 'cp1',
      idTipoAutomacao: TipoAutomacaoEnum.GerarContasPagar,
      icon: 'payments',
      nome: 'Lançar financeiro',
      descricao: 'Gera as contas a pagar a partir da origem escolhida.',
      categoria: 'Financeiro',
      idOrigemLancamento: OrigemLancamentoEnum.NotaFiscal,
      permissao: 'Requer a permissão Inserir do Processo de Compras.',
      permissaoNome: 'Inserir',
      ativa: false,
    },
  ];

  public notificacoes: NotificacaoAutomatica[] = [this.novaNotificacao()];

  // Notificação é o único tipo com várias instâncias por empresa — as demais
  // automações são itens fixos do catálogo, uma por empresa.
  public adicionarNotificacao(): void {
    this.notificacoes.push(this.novaNotificacao());
    this.salvarAutomacoes();
  }

  public removerNotificacao(id: string): void {
    if (this.notificacoes.length <= 1) return;
    this.notificacoes = this.notificacoes.filter(item => item.id !== id);
    this.salvarAutomacoes();
  }

  private novaNotificacao(): NotificacaoAutomatica {
    return {
      id: `notificacao-${this.proximoIdNotificacao++}`,
      icon: 'notifications',
      nome: 'Notificar destinatários',
      descricao: 'Avisa pessoas, perfis ou o responsável/solicitante quando o gatilho ocorrer.',
      categoria: 'Comunicação',
      destinatarios: [],
      idTipoOcorrenciaCompra: null,
      idAutomacao: null,
      ativa: false,
    };
  }

  /**
   * Perfis, usuários e etapas são acessórios: sem eles a tela perde rótulos de destinatário ou
   * a resolução da etapa de destino, mas as automações continuam editáveis. Só a configuração em
   * si é crítica. Cada fonte é blindada individualmente porque um forkJoin que erra descarta
   * TODAS as respostas — a tela ficava mostrando tudo desligado por causa de um lookup.
   */
  private carregar(): void {
    this.carregando = true;
    this.subs.add(
      forkJoin({
        perfis: this.perfilService.getAllLookup().pipe(catchError(() => of(null))),
        usuarios: this.usuarioService.getAll().pipe(catchError(() => of(null))),
        etapas: this.etapasKanbanService.getAll('', false).pipe(catchError(() => of(null))),
        automacoes: this.automacaoService.get().pipe(catchError(() => of(null))),
      }).subscribe({
        next: ({ perfis, usuarios, etapas, automacoes }) => {
          this.carregando = false;

          if (perfis && !perfis.isError) {
            this.destinatariosPorPerfil = (perfis.listaPerfil ?? [])
              .map(item => item.perfil)
              .map(p => ({
                destinatario: { idTipoDestinatarioNotificacao: TipoDestinatarioNotificacaoEnum.Perfil, idPerfil: p.idPerfil },
                label: p.nome,
                icon: this.iconePorPerfil[this.removerAcentos(p.nome.toLowerCase())] ?? 'badge',
                dinamico: false,
              }));
          }

          if (usuarios && !usuarios.isError) {
            this.destinatariosPorUsuario = (usuarios.listaUsuario ?? []).map(u => ({
              destinatario: { idTipoDestinatarioNotificacao: TipoDestinatarioNotificacaoEnum.Usuario, idUsuario: u.idUsuario },
              label: u.nomeUsuario,
              icon: 'person',
              dinamico: false,
            }));
          }

          if (etapas && !etapas.isError) {
            this.idEtapaPorChave = new Map((etapas.listaEtapas ?? []).map(e => [e.chave, e.idEtapaKanban]));
            this.aprovacaoAtiva = (etapas.listaEtapas ?? []).some(e => e.chave === 'aprovacao' && e.isAtivo);
          }

          if (!automacoes || automacoes.isError) {
            this.alertService.show('Erro',
              (automacoes?.errorDescription || 'Não foi possível carregar as automações.')
                + ' Recarregue a página antes de alterar algo — o que está na tela não reflete o servidor.',
              AlertType.error);
            return;
          }
          this.aplicarConfiguracao(automacoes.automacoes || []);
          this.configCarregada = true;
        },
        error: () => {
          this.carregando = false;
          this.alertService.show('Erro', 'Não foi possível carregar as automações.', AlertType.error);
        },
      }),
    );
  }

  /** Sem linha salva a automação fica desligada — o catálogo já nasce assim. */
  private aplicarConfiguracao(automacoes: AutomacaoStruct[]): void {
    for (const acao of this.acoes) {
      const salva = automacoes.find(a => a.idTipoAutomacao === acao.idTipoAutomacao);
      // Linha gravada ativa antes da API passar a desligá-la junto com a etapa: a tela mostra
      // desligada e o próximo salvamento corrige o banco (a API recusaria gravá-la ligada).
      acao.ativa = (salva?.isAtivo ?? false) && !this.acaoBloqueada(acao);
    }

    for (const movimento of this.movimentos) {
      const salva = automacoes.find(a =>
        a.idTipoAutomacao === TipoAutomacaoEnum.MoverAutomaticamente &&
        a.idTipoOcorrenciaCompra === movimento.idTipoOcorrenciaCompra);
      movimento.ativa = salva?.isAtivo ?? false;
      movimento.idEtapaKanbanDestinoSalvo = salva?.idEtapaKanbanDestino ?? null;
    }

    // Gatilho gravado é ignorado (sempre Finalizado); sem origem gravada vale Pela Nota Fiscal.
    for (const lancamento of this.lancamentos) {
      const salvo = automacoes.find(a => a.idTipoAutomacao === lancamento.idTipoAutomacao);
      lancamento.ativa = salvo?.isAtivo ?? false;
      lancamento.idOrigemLancamento = salvo?.idOrigemLancamento === OrigemLancamentoEnum.Pedido
        ? OrigemLancamentoEnum.Pedido
        : OrigemLancamentoEnum.NotaFiscal;
    }

    const notificacoesSalvas = automacoes.filter(a => a.idTipoAutomacao === TipoAutomacaoEnum.Notificar);
    if (notificacoesSalvas.length) {
      this.notificacoes = notificacoesSalvas.map(salva => ({
        ...this.novaNotificacao(),
        idAutomacao: salva.idAutomacao,
        ativa: salva.isAtivo,
        idTipoOcorrenciaCompra: (salva.idTipoOcorrenciaCompra as TipoOcorrenciaCompraEnum) ?? null,
        destinatarios: salva.destinatarios
          .filter(d => this.destinatarioValido(d))
          .map(d => ({ idTipoDestinatarioNotificacao: d.idTipoDestinatarioNotificacao, idPerfil: d.idPerfil ?? null, idUsuario: d.idUsuario ?? null })),
      }));
    }
  }

  /** Perfil/usuário sem id ou tipo desconhecido não tem a quem notificar: fica de fora da tela. */
  private destinatarioValido(destinatario: AutomacaoDestinatarioStruct): boolean {
    switch (destinatario.idTipoDestinatarioNotificacao) {
      case TipoDestinatarioNotificacaoEnum.Responsavel:
      case TipoDestinatarioNotificacaoEnum.Solicitante:
        return true;
      case TipoDestinatarioNotificacaoEnum.Perfil: return !!destinatario.idPerfil;
      case TipoDestinatarioNotificacaoEnum.Usuario: return !!destinatario.idUsuario;
      default: return false;
    }
  }

  /**
   * Grava a configuração a cada mudança — a tela não tem botão Salvar. Uma gravação em voo
   * segura a próxima (`salvarPendente`) em vez de disparar PUTs concorrentes: como o payload
   * é replace-all, duas respostas fora de ordem deixariam o banco no estado antigo.
   */
  public salvarAutomacoes(): void {
    // Sem configuração carregada a tela está com o catálogo desligado: salvar apagaria tudo.
    if (!this.configCarregada) {
      this.alertService.show('Atenção',
        'A configuração atual não pôde ser carregada. Recarregue a página para editar as automações.',
        AlertType.warning);
      return;
    }
    if (this.lancamentos.some(lancamento => this.lancamentoIncompleto(lancamento))) {
      this.alertService.show('Atenção',
        'Escolha a origem (Pela Nota Fiscal ou Pelo Pedido) dos lançamentos ativos antes de salvar.',
        AlertType.warning);
      return;
    }
    if (this.salvando) {
      this.salvarPendente = true;
      return;
    }

    const automacoes: AutomacaoRequest[] = [
      ...this.acoes.map(acao => ({
        idTipoAutomacao: acao.idTipoAutomacao,
        idTipoOcorrenciaCompra: null,
        idEtapaKanbanDestino: null,
        idOrigemLancamento: null,
        isAtivo: acao.ativa,
        destinatarios: [],
      })),
      ...this.movimentos.map(movimento => ({
        idTipoAutomacao: TipoAutomacaoEnum.MoverAutomaticamente,
        idTipoOcorrenciaCompra: movimento.idTipoOcorrenciaCompra,
        idEtapaKanbanDestino: this.idEtapaDestino(movimento),
        idOrigemLancamento: null,
        isAtivo: movimento.ativa,
        destinatarios: [],
      })),
      ...this.lancamentos.map(lancamento => ({
        idTipoAutomacao: lancamento.idTipoAutomacao,
        idTipoOcorrenciaCompra: null,
        idEtapaKanbanDestino: null,
        idOrigemLancamento: lancamento.idOrigemLancamento,
        isAtivo: lancamento.ativa,
        destinatarios: [],
      })),
      ...this.notificacoes.map(notificacao => ({
        idAutomacao: notificacao.idAutomacao,
        idTipoAutomacao: TipoAutomacaoEnum.Notificar,
        idTipoOcorrenciaCompra: notificacao.idTipoOcorrenciaCompra,
        idEtapaKanbanDestino: null,
        idOrigemLancamento: null,
        isAtivo: notificacao.ativa,
        destinatarios: [...notificacao.destinatarios],
      })),
    ];

    // Os ids voltam na mesma ordem do payload: guarda onde as notificações começam para
    // casar cada card com a sua linha na resposta.
    const inicioNotificacoes = automacoes.length - this.notificacoes.length;
    const cardsEnviados = [...this.notificacoes];

    this.salvando = true;
    this.subs.add(
      this.automacaoService.salvar({ automacoes }).subscribe({
        next: (res) => {
          this.salvando = false;
          if (res.isError) {
            this.falhouAoSalvar(res.errorDescription);
            return;
          }
          this.adotarIdsPersistidos(res.idsAutomacao || [], inicioNotificacoes, cardsEnviados);
          this.processarSalvamentoPendente();
        },
        error: () => {
          this.salvando = false;
          this.falhouAoSalvar();
        },
      }),
    );
  }

  /**
   * Notificação criada agora nasceu sem `idAutomacao`; sem o id que o banco gerou, o próximo
   * salvamento a trataria como nova outra vez e recriaria a linha (o backend faz upsert por
   * esse id). Os ids vêm na resposta do próprio PUT, na ordem do payload: a adoção acontece
   * antes de qualquer salvamento enfileirado, então não há corrida.
   *
   * `cardsEnviados` é a lista de cards no momento do envio — se o usuário adicionou ou removeu
   * um card enquanto o PUT estava em voo, o id vai para o card certo (ou para nenhum).
   */
  private adotarIdsPersistidos(ids: number[], inicioNotificacoes: number, cardsEnviados: NotificacaoAutomatica[]): void {
    cardsEnviados.forEach((card, i) => {
      const id = ids[inicioNotificacoes + i];
      if (id != null && card.idAutomacao == null) card.idAutomacao = id;
    });
  }

  private processarSalvamentoPendente(): void {
    if (!this.salvarPendente) return;
    this.salvarPendente = false;
    this.salvarAutomacoes();
  }

  // Sem botão Salvar, o usuário não tem como repetir a ação: recarrega para a tela
  // parar de mostrar um estado que o servidor não tem.
  private falhouAoSalvar(mensagem?: string): void {
    this.salvarPendente = false;
    this.alertService.show('Erro', mensagem || 'Não foi possível salvar as automações.', AlertType.error);
    this.carregar();
  }

  private removerAcentos(texto: string): string {
    return texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
}
